export type ReportObjectIcon =
  | "job"
  | "person"
  | "team"
  | "vendor"
  | "place"
  | "item";

/**
 * Without one the app picks from the record's kind and, for a job or a unit,
 * the trade its names suggest.
 */
export type ReportObjectArt =
  | "job"
  | "hardhat"
  | "van"
  | "crate"
  | "house"
  | "hvac"
  | "radiator"
  | "faucet"
  | "shower"
  | "multimeter"
  | "drain";

export interface ReportObject {
  name: string;
  plural?: string;
  icon: ReportObjectIcon;
  art?: ReportObjectArt;
  /** Where its keys come from: "servicetitan". */
  source?: string;
  detail?: ReportObjectDetail;
}

export interface ReportObjectDetail {
  title?: string;
  subtitle?: string;
  highlights?: string[];
  groups?: { title: string; fields: string[] }[];
  related?: string[];
  history?: string[];
}

/**
 * What a figure means across time: activity in the period (sums), a balance
 * at its end, a running total to date, or a ratio. Only activity adds up; a
 * cumulative job cost summed over weeks is wrong.
 */
export type ReportMeasure = "activity" | "balance" | "cumulative" | "ratio";

export type ReportGrain =
  | "job"
  | "job_line"
  | "job_technician"
  | "technician"
  | "business_unit"
  | "branch"
  | "project"
  | "vendor"
  | "customer"
  | "summary"
  | "custom";

export interface ReportColumnMeaning {
  measure?: ReportMeasure;
  /** Which way is good news: up for revenue, down for a cost. */
  better?: "higher" | "lower";
  /**
   * The key of the actual column (same dataset) this column is the estimate
   * for: `estimated_labor` with `estimate_of: "labor"`. One per actual.
   */
  estimate_of?: string;
}

export interface ReportDatasetMeaning {
  grain?: ReportGrain;
  /** False when rows cannot be matched from one period to the next. */
  trackable?: boolean;
  trend?: "over_time" | "ranked";
}

export const sums = (column: { type: string } & ReportColumnMeaning) => {
  const measure =
    column.measure ?? (column.type === "percent" ? "ratio" : "activity");
  return measure === "activity";
};

export const COLUMN_ROLE = {
  total: "total",
  cost: "cost",
  subtotal: "subtotal",
  result: "result",
  target: "target",
} as const;
export type ReportColumnRole = (typeof COLUMN_ROLE)[keyof typeof COLUMN_ROLE];
